import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  BuildingOfficeIcon,
  CreditCardIcon,
  DocumentTextIcon,
  PlusIcon,
  XMarkIcon,
  ArrowPathIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import api from '../../utils/api';
import { toast } from '../../utils/notification';
import DataTable from '../../components/common/DataTable';

const SUBSCRIPTION_STATUSES = [
  { value: 'trial', label: 'Trial' },
  { value: 'active', label: 'Active' },
  { value: 'past_due', label: 'Past Due' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_BADGE = {
  trial: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  past_due: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  suspended: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

const INVOICE_STATUS_BADGE = {
  paid: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  draft: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  void: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  overdue: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

const formatStatus = (value) =>
  (SUBSCRIPTION_STATUSES.find((s) => s.value === value)?.label) ||
  (value ? value.charAt(0).toUpperCase() + value.slice(1) : '—');

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

const OrganizationsManagement = () => {
  const [organizations, setOrganizations] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  // Manage-subscription modal
  const [subscriptionOrg, setSubscriptionOrg] = useState(null);
  const [subscriptionForm, setSubscriptionForm] = useState({
    subscription_plan: '',
    subscription_status: 'trial',
  });
  const [savingSubscription, setSavingSubscription] = useState(false);

  // Invoices panel (modal)
  const [invoicesOrg, setInvoicesOrg] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);

  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    try {
      const [orgsRes, plansRes] = await Promise.all([
        api.get('/api/admin/organizations/'),
        api.get('/api/accounts/plans/').catch(() => ({ data: [] })),
      ]);
      const orgData = orgsRes?.data?.results || orgsRes?.data || [];
      const planData = plansRes?.data?.results || plansRes?.data || [];
      setOrganizations(Array.isArray(orgData) ? orgData : []);
      setPlans(Array.isArray(planData) ? planData : []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to manage platform billing.');
      } else {
        toast.error('Failed to load organizations');
      }
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // ── Manage subscription ────────────────────────────────────────────
  const openSubscriptionModal = (org) => {
    setSubscriptionOrg(org);
    setSubscriptionForm({
      subscription_plan: org.subscription_plan?.id ? String(org.subscription_plan.id) : '',
      subscription_status: org.subscription_status || 'trial',
    });
  };

  const saveSubscription = async (e) => {
    e.preventDefault();
    if (!subscriptionOrg) return;
    setSavingSubscription(true);
    try {
      await api.patch(`/api/admin/organizations/${subscriptionOrg.id}/subscription/`, {
        subscription_plan: subscriptionForm.subscription_plan
          ? Number(subscriptionForm.subscription_plan)
          : null,
        subscription_status: subscriptionForm.subscription_status,
      });
      toast.success(`Subscription updated for ${subscriptionOrg.name}`);
      setSubscriptionOrg(null);
      await fetchOrganizations();
    } catch (error) {
      console.error('Error updating subscription:', error);
      const msg =
        error.response?.data?.detail ||
        (error.response?.status === 403
          ? 'You do not have permission to change subscriptions.'
          : 'Failed to update subscription');
      toast.error(msg);
    } finally {
      setSavingSubscription(false);
    }
  };

  // ── Invoices ───────────────────────────────────────────────────────
  const fetchInvoices = useCallback(async (orgId) => {
    setInvoicesLoading(true);
    try {
      const res = await api.get(`/api/admin/subscription-invoices/?organization=${orgId}`);
      // DRF-paginated → results array.
      const data = res?.data?.results || res?.data || [];
      setInvoices(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching subscription invoices:', error);
      toast.error('Failed to load subscription invoices');
      setInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  }, []);

  const openInvoicesModal = (org) => {
    setInvoicesOrg(org);
    setInvoices([]);
    fetchInvoices(org.id);
  };

  const generateInvoice = async () => {
    if (!invoicesOrg) return;
    setGeneratingInvoice(true);
    try {
      const res = await api.post('/api/admin/subscription-invoices/generate/', {
        organization: invoicesOrg.id,
        billing_cycle: 'monthly',
      });
      // 201 = newly created, 200 = already existed (idempotent).
      if (res.status === 201) {
        toast.success('Subscription invoice generated.');
      } else {
        toast.info('An invoice for this period already exists.');
      }
      await fetchInvoices(invoicesOrg.id);
    } catch (error) {
      console.error('Error generating subscription invoice:', error);
      const msg = error.response?.data?.detail || 'Failed to generate invoice';
      toast.error(msg);
    } finally {
      setGeneratingInvoice(false);
    }
  };

  // ── Table rows / columns ───────────────────────────────────────────
  const rows = useMemo(
    () =>
      organizations.map((org) => ({
        ...org,
        plan_label: org.subscription_plan?.name || 'No plan',
        status_label: formatStatus(org.subscription_status),
      })),
    [organizations]
  );

  const columns = [
    {
      key: 'name',
      label: 'Organization',
      className: 'text-gray-900 dark:text-white font-medium',
      render: (row) => (
        <div className="flex flex-col">
          <span>{row.name}</span>
          {row.slug && (
            <span className="text-xs text-gray-500 dark:text-gray-400">{row.slug}</span>
          )}
        </div>
      ),
    },
    {
      key: 'plan_label',
      label: 'Plan',
      render: (row) => (
        <div className="flex flex-col">
          <span className="text-gray-900 dark:text-white">{row.plan_label}</span>
          {row.subscription_plan?.tier && (
            <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
              {row.subscription_plan.tier}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'subscription_status',
      label: 'Status',
      sortValue: (row) => row.subscription_status || '',
      render: (row) => (
        <span
          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
            STATUS_BADGE[row.subscription_status] ||
            'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
          }`}
        >
          {row.status_label}
        </span>
      ),
    },
    {
      key: 'member_count',
      label: 'Members',
      render: (row) => row.member_count ?? 0,
    },
    {
      key: 'trial_ends_at',
      label: 'Trial Ends',
      sortValue: (row) => (row.trial_ends_at ? new Date(row.trial_ends_at).getTime() : 0),
      render: (row) => formatDate(row.trial_ends_at),
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      headerClassName: 'text-right',
      className: 'text-right',
      render: (row) => (
        <div className="flex justify-end items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              openSubscriptionModal(row);
            }}
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            title="Manage subscription"
          >
            <CreditCardIcon className="h-4 w-4" /> Subscription
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              openInvoicesModal(row);
            }}
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            title="View subscription invoices"
          >
            <DocumentTextIcon className="h-4 w-4" /> Invoices
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between mb-2">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center">
          <BuildingOfficeIcon className="h-7 w-7 mr-2 text-primary-600 dark:text-primary-400" />
          Organizations &amp; Subscriptions
        </h2>
        <button
          onClick={fetchOrganizations}
          disabled={loading}
          className="mt-3 sm:mt-0 inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md shadow-sm hover:bg-primary-700 disabled:opacity-50"
        >
          <ArrowPathIcon className="h-4 w-4 mr-2" /> Refresh
        </button>
      </div>

      {/* Platform-billing notice */}
      <div className="mb-6 flex items-start gap-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-4 py-3">
        <InformationCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-700 dark:text-blue-300">
          This is <strong>platform billing</strong> (EasyLaw &rarr; firm). It is
          record-keeping only &mdash; no payment is charged here. Generating an
          invoice records what a firm owes for its EasyLaw subscription.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          rowKey="id"
          defaultSort={{ key: 'name', direction: 'asc' }}
          searchPlaceholder="Search organizations by name, plan, or status…"
          searchKeys={['name', 'slug', 'plan_label', 'status_label']}
          emptyMessage="No organizations found."
          emptyIcon={BuildingOfficeIcon}
          pageSize={25}
          pageSizeOptions={[10, 25, 50, 100]}
        />
      )}

      {/* ── Manage Subscription Modal ── */}
      {subscriptionOrg && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div
            className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"
            onClick={() => setSubscriptionOrg(null)}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-xl shadow-2xl">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center">
                  <div className="bg-primary-100 dark:bg-primary-900/30 rounded-lg p-2 mr-3">
                    <CreditCardIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Manage Subscription &mdash; {subscriptionOrg.name}
                  </h3>
                </div>
                <button
                  onClick={() => setSubscriptionOrg(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={saveSubscription} className="p-6 space-y-4">
                <div>
                  <label
                    htmlFor="subscription_plan"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Plan
                  </label>
                  <select
                    id="subscription_plan"
                    value={subscriptionForm.subscription_plan}
                    onChange={(e) =>
                      setSubscriptionForm((prev) => ({
                        ...prev,
                        subscription_plan: e.target.value,
                      }))
                    }
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">No plan</option>
                    {plans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name}
                        {plan.tier ? ` (${plan.tier})` : ''}
                        {plan.monthly_price != null ? ` — $${plan.monthly_price}/mo` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="subscription_status"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Status
                  </label>
                  <select
                    id="subscription_status"
                    value={subscriptionForm.subscription_status}
                    onChange={(e) =>
                      setSubscriptionForm((prev) => ({
                        ...prev,
                        subscription_status: e.target.value,
                      }))
                    }
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                  >
                    {SUBSCRIPTION_STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Record-only change. This sets what the firm is subscribed to on the
                  EasyLaw platform; it does not charge a payment method.
                </p>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setSubscriptionOrg(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingSubscription}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                  >
                    {savingSubscription ? 'Saving…' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── Subscription Invoices Modal ── */}
      {invoicesOrg && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div
            className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"
            onClick={() => setInvoicesOrg(null)}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-3xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center">
                  <div className="bg-primary-100 dark:bg-primary-900/30 rounded-lg p-2 mr-3">
                    <DocumentTextIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Subscription Invoices &mdash; {invoicesOrg.name}
                  </h3>
                </div>
                <button
                  onClick={() => setInvoicesOrg(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Platform billing records (EasyLaw &rarr; firm). No charge is made.
                  </p>
                  <button
                    onClick={generateInvoice}
                    disabled={generatingInvoice}
                    className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    <PlusIcon className="h-4 w-4" />
                    {generatingInvoice ? 'Generating…' : 'Generate Invoice'}
                  </button>
                </div>

                {invoicesLoading ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500"></div>
                  </div>
                ) : invoices.length === 0 ? (
                  <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                    <DocumentTextIcon className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No subscription invoices yet for this organization.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto max-h-[50vh]">
                    <table className="w-full text-sm text-left">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <th className="py-2 px-3 font-medium text-gray-500 dark:text-gray-400">Invoice #</th>
                          <th className="py-2 px-3 font-medium text-gray-500 dark:text-gray-400">Plan</th>
                          <th className="py-2 px-3 font-medium text-gray-500 dark:text-gray-400">Amount</th>
                          <th className="py-2 px-3 font-medium text-gray-500 dark:text-gray-400 hidden sm:table-cell">Cycle</th>
                          <th className="py-2 px-3 font-medium text-gray-500 dark:text-gray-400 hidden md:table-cell">Period</th>
                          <th className="py-2 px-3 font-medium text-gray-500 dark:text-gray-400">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {invoices.map((inv) => (
                          <tr key={inv.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="py-2 px-3 font-mono text-xs text-gray-900 dark:text-white">
                              {inv.invoice_number || `#${inv.id}`}
                            </td>
                            <td className="py-2 px-3 text-gray-700 dark:text-gray-300">
                              {inv.plan_name || '—'}
                            </td>
                            <td className="py-2 px-3 text-gray-900 dark:text-white">
                              {inv.amount != null ? `$${inv.amount}` : '—'}
                            </td>
                            <td className="py-2 px-3 hidden sm:table-cell text-gray-600 dark:text-gray-400 capitalize">
                              {inv.billing_cycle || '—'}
                            </td>
                            <td className="py-2 px-3 hidden md:table-cell text-gray-600 dark:text-gray-400 text-xs">
                              {formatDate(inv.period_start)} &ndash; {formatDate(inv.period_end)}
                            </td>
                            <td className="py-2 px-3">
                              <span
                                className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  INVOICE_STATUS_BADGE[inv.status] ||
                                  'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                }`}
                              >
                                {inv.status
                                  ? inv.status.charAt(0).toUpperCase() + inv.status.slice(1)
                                  : '—'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationsManagement;
